import {
  alpha,
  Autocomplete,
  Avatar,
  Box,
  Chip,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { stringToColor } from 'components/Avatar'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { impersonate } from 'store/session'
import { getClients, } from 'store/lookup'

export default function SchoolsPage() {
  const navigate = useNavigate()
  const { clients: clientList, serviceCampus } = useSelector(state => state.lookup)
  const allDistrictTypes = useSelector(state =>
    state.lookup.types?.filter(tp => tp.category === 'districtType' && tp.code !== 'provider') ?? []
  )
  const serviceCampusTypes = new Set(serviceCampus?.map(sc => sc.type))
  const districtTypes = allDistrictTypes.filter(tp => serviceCampusTypes.has(tp.code))
  const dispatch = useDispatch()
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState(null)

  useEffect(() => {
    if (!clientList) dispatch(getClients())
  }, [clientList, dispatch])

  useEffect(() => {
    if (districtTypes.length && typeFilter === null) {
      const schoolType = districtTypes.find(dt => dt.code === 'school')
      const firstCampusType = serviceCampus?.find(sc => districtTypes.some(dt => dt.code === sc.type))?.type
      setTypeFilter(schoolType ?? districtTypes.find(dt => dt.code === firstCampusType) ?? districtTypes[0])
    }
  }, [districtTypes.length])

  const switchToCampus = async (districtId, campusId) => {
    try {
      await dispatch(impersonate({ districtId, campusId }))
      navigate('/')
    } catch (err) {
      console.error(err)
    }
  }

  const getGradeLevelRange = items => {
    if (!items || items.length === 0) return null
    if (items.length === 1) return items[0]
    return `${items[0]} – ${items[items.length - 1]}`
  }

  if (!clientList || !serviceCampus) return null
  
  const accessByType = {}
  serviceCampus.forEach(sc => {
    if (!accessByType[sc.type]) accessByType[sc.type] = { all: false, ids: new Set() }
    if (sc.campusId === null) accessByType[sc.type].all = true
    else (sc.campusId ?? []).forEach(id => accessByType[sc.type].ids.add(id))
  })

  let schools = []
  clientList.forEach(item => {
    if (typeFilter && item.client.type !== typeFilter.code) return
    const access = accessByType[item.client.type]
    if (!access) return
    item.client.schools.forEach(school => {
      const campuses = []
      school.campuses.forEach(campus => {
        if (!access.all && !access.ids.has(campus.id)) return
        const te = campus.academicYears?.[0]?.campusTargetedEnrollments?.[0]?.targetedEnrollments
        const gradeLevels = te?.length
          ? te.map(t => t.gradeLevelId)
          : campus.gradeLevelIds
        campuses.push({
          id: campus.id,
          name: campus.name,
          gradeLevelIds: gradeLevels,
          districtId: item.client.id,
          abbreviation: item.client.abbreviation,
        })
      })
      if (campuses.length > 0) schools.push({
        id: school.id,
        name: school.name,
        campuses,
        districtName: item.client.name,
        abbreviation: item.client.abbreviation,
        color: stringToColor(item.client.abbreviation),
      })
    })
  })

  const filtered = search.trim()
    ? schools.filter(s =>
        s.name.toLowerCase().includes(search.toLowerCase()) ||
        s.districtName.toLowerCase().includes(search.toLowerCase()) ||
        s.campuses.some(c => c.name.toLowerCase().includes(search.toLowerCase()))
      )
    : schools

  return (
    <Box sx={{ pt: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <TextField
          placeholder="Search schools or campuses…"
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ width: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Autocomplete
          size="small"
          options={districtTypes}
          getOptionLabel={option => option.name ?? ''}
          value={typeFilter}
          onChange={(_, value) => setTypeFilter(value)}
          sx={{ width: 200 }}
          renderInput={params => <TextField {...params} label="Type" />}
          disableClearable
        />
      </Stack>

      <Grid container spacing={2}>
        {filtered.map(school => (
          <Grid key={school.id} item xs={12} sm={6} xl={4}>
            <Paper
              elevation={2}
              sx={{ borderRadius: 2, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              {/* Card header */}
              <Box
                sx={{
                  bgcolor: school.color,
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: alpha('#fff', 0.25),
                    color: '#fff',
                    width: 40,
                    height: 40,
                    fontSize: 14,
                    fontWeight: 700,
                  }}
                >
                  iLS
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: alpha('#fff', 0.8), display: 'block', lineHeight: 1.2 }}
                  >
                    {school.districtName}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ color: '#fff', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {school.name}
                  </Typography>
                </Box>
              </Box>

              {/* Campus list */}
              <Box sx={{ flex: 1 }}>
                {school.campuses.map((campus, idx) => (
                  <Box key={campus.id}>
                    {idx > 0 && <Divider />}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      onClick={() => switchToCampus(campus.districtId, campus.id)}
                      sx={{
                        px: 2,
                        py: 1.25,
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                        '&:hover': {
                          bgcolor: alpha(school.color, 0.08),
                          '& .chevron': { opacity: 1, transform: 'translateX(2px)' },
                        },
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {campus.name}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {campus.gradeLevelIds && (
                          <Chip
                            label={`Gr. ${getGradeLevelRange(campus.gradeLevelIds)}`}
                            size="small"
                            sx={{
                              bgcolor: alpha(school.color, 0.12),
                              color: school.color,
                              fontWeight: 600,
                              fontSize: 11,
                              height: 22,
                            }}
                          />
                        )}
                        <ChevronRightIcon
                          className="chevron"
                          sx={{
                            fontSize: 18,
                            color: 'text.secondary',
                            opacity: 0.4,
                            transition: 'opacity 0.15s, transform 0.15s',
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  px: 2,
                  py: 0.75,
                  bgcolor: 'action.hover',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {school.campuses.length} {school.campuses.length === 1 ? 'campus' : 'campuses'}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}

        {filtered.length === 0 && (
          <Grid item xs={12}>
            <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 6 }}>
              No schools match {search}

            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}
